import { IPDB_CONTROL_WELLS_LAYER, IPDB_LAYER } from "constants/mapLayers.constants";

import { UserEventContext } from "api/userEvents";

import { EntityKind } from "models/entityKind";
import { IScreenshotSettings } from "models/screenshot";

import { WidgetContextExtractor } from "components/screenshot/utils/widgetContextExtractors";

const HIGHLIGHTED_NETWORK_FACILITIES = "highlighted-network-facilities";

interface MapboxSource {
  tiles?: string[];
}

interface MapboxLayer {
  id: string;
  source: string;
  type: string;
  sourceLayer?: string;
}

interface MapboxInstance {
  getZoom(): number;
  getLayer(id: string): unknown;
  getSource(id: string): MapboxSource | undefined;
  getStyle(): { layers: MapboxLayer[] } | undefined;
}

interface MapWidgetState {
  activeEntityKinds?: EntityKind[];
  mapbox?: MapboxInstance;
}

/**
 * Map-specific context extractor for screenshot events
 * Extracts relevant map information like zoom level, layers, visualization settings, etc.
 */
export class MapContextExtractor implements WidgetContextExtractor {
  widgetType = "map";

  extractContext(
    widgetState: MapWidgetState,
    settings: IScreenshotSettings
  ): UserEventContext {
    const context: UserEventContext = {};

    // Extract entity kind information
    this.extractEntityKindContext(widgetState, settings, context);

    // Extract map view information
    this.extractMapViewContext(widgetState, settings, context);

    // Extract layer information
    this.extractLayerContext(widgetState, context);

    // Extract IPDB information
    this.extractIpdbContext(widgetState, context);

    return context;
  }

  private extractEntityKindContext(
    widgetState: MapWidgetState,
    settings: IScreenshotSettings,
    context: UserEventContext
  ): void {
    const upstreamEnabled = widgetState?.activeEntityKinds?.includes(EntityKind.Well);
    const midstreamEnabled = widgetState?.activeEntityKinds?.includes(
      EntityKind.Facility
    );

    context.upstreamEnabled = !!upstreamEnabled;
    context.midstreamEnabled = !!midstreamEnabled;
    context.legendsEnabled =
      settings?.showMidstreamLegendInScreenshot || settings?.showLegendInScreenshot;
    context.midstreamLegendsEnabled =
      !!midstreamEnabled && !!settings?.showMidstreamLegendInScreenshot;
    context.upstreamLegendsEnabled =
      !!upstreamEnabled && !!settings?.showLegendInScreenshot;
  }

  private extractMapViewContext(
    widgetState: MapWidgetState,
    settings: IScreenshotSettings,
    context: UserEventContext
  ): void {
    context.zoom = widgetState?.mapbox?.getZoom() ?? "unknown";
    context.showTownshipBorder = settings?.showTownshipBorderInScreenshot;
    context.scale = settings?.mapScreenshotScale;
    context.showingFacilityNetwork = !!widgetState?.mapbox?.getLayer(
      HIGHLIGHTED_NETWORK_FACILITIES
    );
  }

  private extractLayerContext(
    widgetState: MapWidgetState,
    context: UserEventContext
  ): void {
    const allLayers = widgetState?.mapbox?.getStyle()?.layers || [];

    // Count shapefile layers
    // Note: This is a heuristic solution - shapefile layers typically have:
    // - Same id and source
    // - Type 'fill'
    // - No sourceLayer property
    // - Not the IPDB layer
    const shapefileLayerCount = allLayers.reduce((count, layer) => {
      const isShapefileLayer =
        layer.id !== IPDB_LAYER &&
        layer.source === layer.id &&
        layer.type === "fill" &&
        !layer.sourceLayer;

      return isShapefileLayer ? count + 1 : count;
    }, 0);

    context.shapefileLayerCount = shapefileLayerCount;
  }

  private extractIpdbContext(
    widgetState: MapWidgetState,
    context: UserEventContext
  ): void {
    context.ipdbEnabled = !!widgetState?.mapbox?.getLayer(IPDB_LAYER);
    context.ipdbControlWellsEnabled = !!widgetState?.mapbox?.getLayer(
      IPDB_CONTROL_WELLS_LAYER
    );

    if (!context.ipdbEnabled) {
      return;
    }

    const source = widgetState?.mapbox?.getSource("IPDB");
    const ipdbTileUrl = source?.tiles?.[0];

    if (!ipdbTileUrl) {
      return;
    }

    try {
      const ipdbData = this.parseIpdbUrl(ipdbTileUrl);
      if (ipdbData) {
        Object.assign(context, ipdbData);
      }
    } catch (error) {
      // If parsing fails, just note that IPDB is enabled without details
      // eslint-disable-next-line no-console
      console.warn("Failed to parse IPDB URL:", error);
    }
  }

  private parseIpdbUrl(url: string): Partial<UserEventContext> | null {
    // Parse URL pattern: /ipdb/{base64_field}/{model}/{base64_zones}/{z}/{x}/{y}.mvt
    const urlMatch = url.match(/\/ipdb\/([^/]+)\/([^/]+)\/([^/]+)\/\{z\}/);

    if (!urlMatch) {
      return null;
    }

    const [, encodedField, model, encodedZones] = urlMatch;

    // Decode base64 components
    const field = atob(encodedField);
    const zones = JSON.parse(atob(encodedZones));

    return {
      ipdbField: field,
      ipdbModel: model,
      ipdbZones: zones,
      ipdbZoneCount: Array.isArray(zones) ? zones.length : 0
    };
  }
}
